import React, { useState } from 'react';
import { strings } from '/imports/strings';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { useFind, useSubscription, useUser } from '/imports/hooks';
import { Dict } from '/imports/types';
import { queryStringToObject } from '/imports/utils';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { Conversations } from '/imports/api/conversations';
import { Publications } from '/imports/publications';
import { ConversationFeed } from '/imports/ui/ConversationFeed';
import { MessageReceipt, MessageReceipts } from '/imports/api/messages';

const MessagesPageStyled = (props: React.ComponentProps<typeof Page>) => (
  <Page
    {...props}
    className={[props.className ?? '', '[&_.page-heading_.left]:ml-auto'].join(' ')}
  />
);

export const MessagesPage = () => {
  const user = useUser();
  const query = queryStringToObject();
  const [conversationId, setConversationId] = useState<string | undefined>(query.c || undefined);
  const loadingConversations = useSubscription(Publications.conversations);
  const conversations = useFind(() => Conversations.find({}, { sort: { lastMessageAt: -1 } }), [loadingConversations]);
  const loadingMessageReceipts = useSubscription(Publications.unreadMessageReceipts);
  const unreadReceipts = useFind(() => MessageReceipts.find(), [loadingMessageReceipts]);
  const loading = loadingConversations || loadingMessageReceipts;
  const unreadByConversation = unreadReceipts.reduce((map: Dict<MessageReceipt[]>, r) => {
    const conversationId = r.conversationId;
    if (!map[conversationId]) map[conversationId] = [];
    map[conversationId].push(r);
    return map;
  }, {});
  return (
    <MessagesPageStyled>
      <PageHeading
        left={
          <h1>{strings.Messages()}</h1>
        }
      />
      <PageBody>
        {
          loading ?
            <LoadingFlex />
            :
            <div>
              {
                conversations.map((c) => {
                  return (
                    <div key={c._id} className="conversation relative cursor-pointer border-b border-grey-4 py-4 pr-4 pl-8 first:border-t first:border-grey-4" onClick={() => setConversationId(c._id)}>
                      <div className="name font-bold">{c.name}</div>
                      {
                        c.lastMessage ?
                          <div className="last-message max-w-full overflow-x-hidden text-ellipsis whitespace-nowrap text-grey-4">
                            {c.lastMessage?.senderUserId === user?._id ? strings.You() : c.lastMessage?.senderOrgName}: {c.lastMessage?.content}
                          </div>
                          :
                          <div className="last-message max-w-full overflow-x-hidden text-ellipsis whitespace-nowrap text-grey-4">&nbsp;</div>
                      }
                      {
                        unreadByConversation[c._id]?.length ?
                          <div className="badge absolute top-[0.1rem] right-0 h-6 min-w-6 rounded-xl bg-primary text-center text-on-primary">{unreadByConversation[c._id].length}</div>
                          :
                          null
                      }
                    </div>
                  );
                })
              }
            </div>
        }
      </PageBody>
      {
        conversationId ?
          <ConversationFeed
            key={conversationId}
            conversationId={conversationId}
            onClose={() => setConversationId(undefined)}
          />
          :
          null
      }
    </MessagesPageStyled>
  );
};